import {
    Card,
    Stack,
    Avatar,
    Typography,
    Link,
    Button,
    Chip,
    Box,
} from "@mui/material";
import React from "react";
import avatarAlt from "../../utils/avatarAlt";
import StarIcon from "@mui/icons-material/Star";
import toRibuan from "../../utils/toRibuan";
import toRupiah from "../../utils/toRupiah";
import EditIcon from "@mui/icons-material/Edit";
import PrintIcon from "@mui/icons-material/Print";
import DeleteIcon from "@mui/icons-material/Delete";
import { ModalStokField, ContentGetItemResponse } from "../../constants/types";

import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import IconButton from "@mui/material/IconButton";

interface ICardStokBarangProps {
    data: ContentGetItemResponse;
    handleSelectedProducts: (
        id: number,
        nama: string,
        harga: number,
        stok: number,
    ) => void;
    isSelected: (id: number) => boolean;
    onEdit: (data: ModalStokField) => void;
    onDelete: (id: number | string, nama: string) => void;
    onOpenDetail: (namaBarang: string, url: string, deskripsi?: string) => void;
    onOpenPrintBarcode: (data: {
        sku?: string | null;
        namaBarang: string;
        hargaBarang: string;
    }) => void;
}

const CardStokBarang = ({
    data,
    handleSelectedProducts,
    isSelected,
    onEdit,
    onDelete,
    onOpenDetail,
    onOpenPrintBarcode,
}: ICardStokBarangProps) => {
    const {
        id,
        namaBarang,
        sku,
        stok: jumlahStok,
        deskripsi,
        harga,
        gambar: url,
        kategori: kategoriId,
        isActiveStok,
        itemSatuan,
        isBundling,
        detailBundling,
        favorite: isFavorite,
        hargaMultiple: multiHarga,
        isShow,
    } = data;
    const kategori = kategoriId.nama;
    const favorite = isFavorite === 1 ? true : false;
    const hargaMultiple = multiHarga.map((li) => ({
        id: li.id,
        harga: Number(li.harga),
        keterangan: li.keterangan,
    }));

    const truncate = (str: string) => {
        return str.length > 60 ? str.substring(0, 60) + "..." : str;
    };

    return (
        <React.Fragment>
            <Card
                variant="outlined"
                sx={{
                    marginBottom: 1,
                    padding: 2,
                }}
            >
                <Chip label={kategori} color="primary" size="small" />
                <Stack
                    className="card-barang"
                    direction="column"
                    spacing={1}
                    justifyContent="space-between"
                    marginTop={1}
                >
                    <Stack direction="row" alignItems="center">
                        <IconButton
                            disabled={jumlahStok <= 0 && jumlahStok !== null}
                            onClick={() =>
                                handleSelectedProducts(
                                    id,
                                    namaBarang,
                                    harga,
                                    jumlahStok,
                                )
                            }
                            sx={{
                                minWidth: "unset",
                                padding: "6px",
                                width: "fit-content",
                                marginRight: 2,
                            }}
                            size="small"
                            color="primary"
                        >
                            {isSelected(id) ? (
                                <CheckBoxIcon />
                            ) : (
                                <CheckBoxOutlineBlankIcon />
                            )}
                        </IconButton>

                        <Link
                            onClick={() => onOpenDetail(namaBarang, url, deskripsi)}
                            color="#000000"
                            underline="none"
                            sx={{ cursor: "pointer" }}
                        >
                            <Stack direction="row" spacing={2}>
                                <Avatar
                                    sx={{
                                        backgroundColor: "primary.main",
                                        width: 78,
                                        height: 78,
                                        borderRadius: "10px",
                                    }}
                                    variant="square"
                                    alt={namaBarang}
                                    src={url}
                                >
                                    {avatarAlt(namaBarang)}
                                </Avatar>
                                {favorite === true && (
                                    <StarIcon
                                        sx={{
                                            position: "relative",
                                            zIndex: 2,
                                            bottom: 8,
                                            left: -34,
                                            border: 1,
                                            borderColor: "#e7e5e4",
                                            borderRadius: "50%",
                                            padding: 0.1,
                                            color: "#fbbf24",
                                            bgcolor: "#FFFFFF",
                                        }}
                                        fontSize="medium"
                                    />
                                )}
                                <Box>
                                    <Typography variant="subtitle2">
                                        {sku}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        fontWeight="bold"
                                    >
                                        {namaBarang}
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        fontWeight="bold"
                                    >
                                        {toRupiah(harga)}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Link>
                    </Stack>

                    <Link
                        onClick={() => onOpenDetail(namaBarang, url, deskripsi)}
                        color="#000000"
                        underline="none"
                        sx={{ cursor: "pointer" }}
                    >
                        <Stack direction="column" marginTop={1} spacing={1}>
                            <Typography variant="body2" component="div">
                                Stok Tersedia:{" "}
                                <Typography
                                    variant="body2"
                                    fontWeight="bold"
                                    color={
                                        jumlahStok < 5 && jumlahStok !== null
                                            ? "#ef4444"
                                            : "#76747C"
                                    }
                                    display="inline"
                                >
                                    {jumlahStok === null
                                        ? "Tidak Aktif"
                                        : jumlahStok > 5
                                          ? toRibuan(jumlahStok)
                                          : jumlahStok <= 5 && jumlahStok > 0
                                            ? toRibuan(jumlahStok)
                                            : "Habis"}
                                </Typography>
                            </Typography>
                            <Typography variant="body2">
                                {truncate(deskripsi ?? "Tidak ada deskripsi")}
                            </Typography>
                        </Stack>
                    </Link>
                    <Stack
                        display="flex"
                        alignItems="flex-end"
                        justifyContent="flex-end"
                        direction="row"
                    >
                        <Stack direction="row" spacing={2}>
                            <Button
                                variant="outlined"
                                onClick={() =>
                                    onEdit({
                                        id: id,
                                        namaBarang: namaBarang,
                                        deskripsiBarang: deskripsi,
                                        kategoriId: kategoriId,
                                        harga: harga,
                                        sku: sku,
                                        jumlahStok: jumlahStok,
                                        isActiveStok: isActiveStok,
                                        itemSatuan: itemSatuan,
                                        isBundling,
                                        detailBundling,
                                        favorite: favorite,
                                        hargaMultiple: hargaMultiple,
                                        isShow: isShow === 1,
                                    })
                                }
                                size="small"
                                sx={{
                                    minWidth: "unset",
                                    padding: "6px",
                                    width: "fit-content",
                                }}
                            >
                                <EditIcon />
                            </Button>
                            <Button
                                variant="contained"
                                onClick={() =>
                                    onOpenPrintBarcode({
                                        sku,
                                        namaBarang,
                                        hargaBarang: toRupiah(harga),
                                    })
                                }
                                size="small"
                                sx={{
                                    minWidth: "unset",
                                    padding: "6px",
                                    width: "fit-content",
                                }}
                            >
                                <PrintIcon />
                            </Button>
                            <Button
                                variant="contained"
                                onClick={() =>
                                    onDelete(id, namaBarang)
                                }
                                size="small"
                                color="error"
                                sx={{
                                    minWidth: "unset",
                                    padding: "6px",
                                    width: "fit-content",
                                }}
                            >
                                <DeleteIcon />
                            </Button>
                        </Stack>
                    </Stack>
                </Stack>
            </Card>
        </React.Fragment>
    );
};

export default CardStokBarang;
